<template>
  <DefaultLayout>
    <div slot="content">
      <div class="d-flex align-center mb-3">
        <div class="title">PV Vault Stats</div>
      </div>
      <v-divider></v-divider>
      <v-row class="mt-3 mb-2">
        <v-col cols="12" sm="6" md="4">
          <v-select
            dense
            class="body-2"
            :items="datesRelative"
            prepend-icon="date_range"
            id="filter-date-select"
            item-value="value"
            v-model="datesRelativeSelected"
            @change="updateRelativeDate"
            label="Filter by date"
          >
          </v-select>
        </v-col>
      </v-row>
      <div v-if="loading" class="text-center pt-12 mt-12 headline">
        <v-progress-circular indeterminate color="blue-grey" width="2" size="50">
        </v-progress-circular>
      </div>
      <div v-if="dataHistory && !loading" style="min-height: 400px" class="mb-12">
        <div class="d-flex align-center mb-3">
          <div class="subtitle">PV Vault activity (days, events)</div>
        </div>
        <ECharts :options="chartOptions0" autoresize style="width: 100%; height: 400px" />
      </div>
      <v-row v-if="dataHistory && !loading">
        <v-col v-for="(data, index) in userStoresData" :key="index + data.name" cols="4">
          <h3 class="text-center text-h6 mb-4">{{ data.name }}</h3>
          <v-data-table
            v-if="data.items.length"
            :headers="headers"
            :items="data.items"
            class="elevation-1 rounded-lg"
          >
            <template #item.dateCreated="{ item }">
              <v-chip class="mr-2" color="primary" small outlined>
                {{ new Date(item.dateCreated).toLocaleDateString() }}
              </v-chip>
            </template>
            <template #item.eventsCount="{ item }">
              <p>{{ item.eventsCount }}</p>
            </template>
          </v-data-table>
          <v-card v-else class="rounded-lg" :height="auto">
            <div class="text-center pt-8 pb-4">
              <v-avatar size="62" color="blue">
                <v-icon dark> mdi-database-off-outline</v-icon>
              </v-avatar>
            </div>
            <v-card-text class="text-center">No data available</v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </DefaultLayout>
</template>

<script>
import { REQUEST_ACCOUNT_TRACKING_DATA } from '@/store/actions.type';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import DefaultLayout from '@/layouts/DefaultLayout';
import ECharts from 'vue-echarts';

export default {
  name: 'account-tracking',
  components: {
    DefaultLayout,
    ECharts
  },
  data() {
    return {
      datesRelativeSelected: 30,
      datesRelative: [
        { value: 7, text: 'Last 7 days' },
        { value: 30, text: 'Last 1 month' },
        { value: 60, text: 'Last 2 months' },
        { value: 180, text: 'Last 6 months' },
        { value: null, text: 'All time' }
      ],
      loading: true,
      dataHistory: {},
      headers: [
        {
          text: 'Date',
          value: 'dateCreated',
          sortable: true,
          isSlotable: true
        },
        {
          text: 'Events',
          value: 'eventsCount',
          sortable: true,
          isSlotable: true
        }
      ],
      chartOptions0: {}
    };
  },
  computed: {
    userStoreVisit() {
      return this.dataHistory.userStoreVisit || [];
    },
    userStoreShare() {
      return this.dataHistory.userStoreShare || [];
    },
    userStoreAdded() {
      return this.dataHistory.userStoreAdded || [];
    },
    userStoresData() {
      return [
        { name: 'PV Vault Visited', items: this.userStoreVisit },
        { name: 'PV Vault Shared', items: this.userStoreShare },
        { name: 'PV Vault Added', items: this.userStoreAdded }
      ];
    }
  },
  methods: {
    getCurrentDateTimeDelta() {
      const MS_IN_DAY = 24 * 60 * 60 * 1000;
      return this.datesRelativeSelected !== null
        ? Date.now() - this.datesRelativeSelected * MS_IN_DAY
        : 0;
    },
    updateRelativeDate() {
      this.fetchRequests();
    },
    prepareChartOptions() {
      const allData = [
        ...this.userStoreVisit.map((item) => ({
          date: item.dateCreated,
          visitedCount: item.eventsCount
        })),
        ...this.userStoreShare.map((item) => ({
          date: item.dateCreated,
          sharedCount: item.eventsCount
        })),
        ...this.userStoreAdded.map((item) => ({
          date: item.dateCreated,
          addedCount: item.eventsCount
        }))
      ];

      const dateMap = {};

      allData.forEach(({ date, visitedCount = 0, sharedCount = 0, addedCount = 0 }) => {
        const key = new Date(date).toLocaleDateString();
        if (!dateMap[key]) {
          dateMap[key] = { visitedCount: 0, sharedCount: 0, addedCount: 0 };
        }
        dateMap[key].visitedCount += visitedCount;
        dateMap[key].sharedCount += sharedCount;
        dateMap[key].addedCount += addedCount;
      });

      const sortedDates = Object.keys(dateMap).sort((a, b) => new Date(a) - new Date(b));

      this.chartOptions0 = {
        tooltip: { trigger: 'axis' },
        legend: { data: ['Stores Visited', 'Stores Shared', 'Stores Added'] },
        xAxis: { type: 'category', data: sortedDates },
        yAxis: { type: 'value' },
        color: ['#4B90EB', '#2CA6A4', '#D36708'],
        series: [
          {
            name: 'Stores Visited',
            type: 'line',
            smooth: true,
            areaStyle: {opacity: 0.2},
            data: sortedDates.map((date) => dateMap[date].visitedCount)
          },
          {
            name: 'Stores Shared',
            type: 'line',
            smooth: true,
            areaStyle: {},
            data: sortedDates.map((date) => dateMap[date].sharedCount)
          },
          {
            name: 'Stores Added',
            type: 'line',
            smooth: true,
            areaStyle: {},
            data: sortedDates.map((date) => dateMap[date].addedCount)
          }
        ]
      };
    },
    async fetchRequests() {
      this.loading = true;
      const data = {
        from: this.getCurrentDateTimeDelta(),
        to: Date.now()
      };

      try {
        const response = await this.$store.dispatch(REQUEST_ACCOUNT_TRACKING_DATA, data);
        this.dataHistory = response;
        this.prepareChartOptions();
        this.loading = false;
      } catch (err) {
        this.loading = false;
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Something went wrong - please contact support@planetverify.com'
        });
      }
    }
  },
  mounted() {
    this.fetchRequests();
  }
};
</script>
